import { ReactNode } from "react";
import { Outlet } from "react-router-dom";

interface AuthLayoutProps {
  children?: ReactNode;
  variant?: "dots" | "geometric" | "grid" | "waves";
}

export default function AuthLayout({
  children,
  variant = "dots",
}: AuthLayoutProps) {
  const getPatternStyles = () => {
    switch (variant) {
      case "dots":
        return {
          backgroundImage: `radial-gradient(circle, hsl(var(--p)) 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
        };
      case "geometric":
        return {
          backgroundImage: `
            linear-gradient(135deg, hsl(var(--p) / 0.05) 25%, transparent 25%),
            linear-gradient(-135deg, hsl(var(--p) / 0.05) 25%, transparent 25%),
            linear-gradient(45deg, hsl(var(--s) / 0.05) 25%, transparent 25%),
            linear-gradient(-45deg, hsl(var(--s) / 0.05) 25%, transparent 25%)
          `,
          backgroundPosition: "0 0, 0 20px, 20px -20px, -20px 0px",
          backgroundSize: "40px 40px",
        };
      case "grid":
        return {
          backgroundImage: `
            linear-gradient(hsl(var(--p) / 0.1) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--p) / 0.1) 1px, transparent 1px)
          `,
          backgroundSize: "24px 24px",
        };
      case "waves":
        return {
          backgroundImage: `
            radial-gradient(ellipse at top, hsl(var(--p) / 0.1), transparent),
            radial-gradient(ellipse at bottom, hsl(var(--s) / 0.1), transparent)
          `,
        };
      default:
        return {};
    }
  };

  return (
    <div className="flex min-h-screen bg-base-100">
      {/* Left Side - Form */}
      <div className="flex flex-1 items-center justify-center bg-base-100 p-4 lg:p-8">
        <div className="w-full max-w-md">{children || <Outlet />}</div>
      </div>

      {/* Right Side - Pattern Background */}
      <div className="relative hidden bg-base-200 lg:flex lg:flex-1">
        {/* Animated Pattern Background */}
        <div
          className="absolute inset-0 opacity-30"
          style={getPatternStyles()}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10" />

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/4 top-1/4 h-32 w-32 animate-pulse rounded-full bg-primary/5 blur-xl" />
          <div className="absolute right-1/4 top-3/4 h-24 w-24 animate-pulse rounded-full bg-secondary/5 blur-xl delay-1000" />
          <div className="absolute right-1/3 top-1/2 h-40 w-40 animate-pulse rounded-full bg-accent/5 blur-xl delay-500" />
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-center">
          <div className="max-w-lg">
            <div className="mb-8">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 backdrop-blur-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
                  <svg
                    className="h-8 w-8 text-primary-content"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
              </div>
            </div>

            <h1 className="mb-4 text-4xl font-bold text-base-content">
              Welcome to Hareeng
            </h1>
            <p className="mb-8 text-lg leading-relaxed text-base-content/70">
              Your comprehensive dashboard for managing rides, deliveries, and
              business operations with powerful analytics and insights.
            </p>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="rounded-xl border border-base-content/5 bg-base-100/50 p-4 backdrop-blur-sm transition-colors hover:border-primary/20">
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <svg
                    className="h-5 w-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-base-content">
                  Analytics
                </h3>
                <p className="text-xs leading-relaxed text-base-content/60">
                  Real-time insights and comprehensive reporting
                </p>
              </div>

              <div className="rounded-xl border border-base-content/5 bg-base-100/50 p-4 backdrop-blur-sm transition-colors hover:border-secondary/20">
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/10">
                  <svg
                    className="h-5 w-5 text-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-base-content">
                  User Management
                </h3>
                <p className="text-xs leading-relaxed text-base-content/60">
                  Comprehensive user administration
                </p>
              </div>

              <div className="rounded-xl border border-base-content/5 bg-base-100/50 p-4 backdrop-blur-sm transition-colors hover:border-accent/20">
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
                  <svg
                    className="h-5 w-5 text-accent"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-base-content">
                  Fleet Control
                </h3>
                <p className="text-xs leading-relaxed text-base-content/60">
                  Monitor vehicles and drivers
                </p>
              </div>

              <div className="rounded-xl border border-base-content/5 bg-base-100/50 p-4 backdrop-blur-sm transition-colors hover:border-primary/20">
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <svg
                    className="h-5 w-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-base-content">
                  Ride Management
                </h3>
                <p className="text-xs leading-relaxed text-base-content/60">
                  Track and manage all bookings
                </p>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 border-t border-base-content/10 pt-6">
              <div className="flex items-center justify-center space-x-6 text-xs text-base-content/50">
                <div className="flex items-center">
                  <svg
                    className="mr-1 h-4 w-4 text-success"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      fillRule="evenodd"
                    />
                  </svg>
                  Secure
                </div>
                <div className="flex items-center">
                  <svg
                    className="mr-1 h-4 w-4 text-info"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Reliable
                </div>
                <div className="flex items-center">
                  <svg
                    className="mr-1 h-4 w-4 text-warning"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      fillRule="evenodd"
                    />
                  </svg>
                  Fast
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
